import { useCallback, useEffect, useId, useRef, useState } from 'react';

export const usePopover = <Panel extends HTMLElement>() => {
  const [open, setOpen] = useState(false);
  const [focusRequests, setFocusRequests] = useState(0);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<Panel>(null);
  const panelId = useId();

  const close = useCallback(() => {
    setOpen(false);
    setFocusRequests((value) => value + 1);
  }, []);

  const toggle = useCallback(() => setOpen((value) => !value), []);

  useEffect(() => {
    if (focusRequests === 0) return;
    triggerRef.current?.focus();
  }, [focusRequests]);

  useEffect(() => {
    if (!open) return undefined;
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') close();
    };
    const closeOnOutsidePress = (event: MouseEvent) => {
      const target = event.target as Node | null;
      if (!target || panelRef.current?.contains(target) || triggerRef.current?.contains(target))
        return;
      setOpen(false);
    };
    document.addEventListener('keydown', closeOnEscape);
    document.addEventListener('mousedown', closeOnOutsidePress);
    return () => {
      document.removeEventListener('keydown', closeOnEscape);
      document.removeEventListener('mousedown', closeOnOutsidePress);
    };
  }, [close, open]);

  return { open, close, toggle, panelId, panelRef, triggerRef };
};
